import {
    AlertColor,
    Button,
    CircularProgress,
    Modal,
    TextField,
    Theme,
    Typography,
    useMediaQuery,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { globalTheme } from "../../../common/theme";
import createStyles from "@mui/styles/createStyles";
import axios from "axios";
import React, { useImperativeHandle } from "react";
import { SnackBarMessage, SnackBarMessageProps } from "../../component/SnackBarMessage";
import Title from "../../component/Title";
import { BranchInfo } from "./BranchAccessories";
import { renewToken } from "../../../services/AuthService";
import { OpenMode } from "../../../common/Enums";
import { GeneralDialog } from "../../component/GeneralDialog";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
    })
);

export interface BranchProps {
    onClose: Function;
}

export interface BranchSingleHandle {
    Initialise: (mode: OpenMode, branch: BranchInfo) => void;
}

export const BranchSingle = React.forwardRef(
    (props: BranchProps, ref: React.Ref<BranchSingleHandle>) => {
        const style = { ...useStyles(), ...globalTheme() };

        const tinyScreen = useMediaQuery('(max-width:1366px)');
        const smallScreen = useMediaQuery('(max-width:1920px)');
        const normalScreen = useMediaQuery('(max-width:2560px)');

        const [open, setOpen] = React.useState(false);
        const [openMode, setOpenMode] = React.useState<OpenMode>(OpenMode.NEW);
        const [loading, setLoading] = React.useState<boolean>(false);
        const [hasError, setHasError] = React.useState(false);
        const [branch, setBranch] = React.useState<BranchInfo>();
        const [originalBranch, setOriginalBranch] = React.useState<BranchInfo>();
        const [saved, setSaved] = React.useState<boolean>(false);

        //Dialogs and snacks
        const hasChanges = React.useRef<boolean>(false);
        const [dialogOpen, setDialogOpen] = React.useState<boolean>(false);
        const [snackSettings, setSnackSettings] =
        React.useState<SnackBarMessageProps>({
            open: false,
            message: "",
            subMessage: "",
            severity: undefined,
            onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
        });

        function showSnackBar(message: string, subMessage: string, colour?: AlertColor) {
            setSnackSettings({
                open: true,
                message: message,
                subMessage: subMessage,
                severity: colour,
                onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
            });
        }

        useImperativeHandle(ref, () => ({
            Initialise(mode: OpenMode, branch: BranchInfo) {
                setOpen(true);
                setSaved(false);
                renewToken();
        
                hasChanges.current = false;
                setOpenMode(mode);

                if (branch)
                {
                    setBranch(branch);
                    setOriginalBranch(branch);
                }
            },
        }));

        function closeModal() {
            setDialogOpen(false);
            setOpen(false);
            props.onClose(saved, originalBranch);
        }

        function closeModalCheck(keepOpen: boolean) {
            if (hasChanges.current) {
              setDialogOpen(true);
            } else {
              setDialogOpen(false);
              setOpen(keepOpen);
              props.onClose(saved, originalBranch);
            }
        }

        function OnBranchChanged(
            propertyName: string,
            propertyValue: any,
            validationRegex?: RegExp
        ) {
            let proceed = true;
        
            if (validationRegex !== undefined) {
                proceed = validationRegex.test(propertyValue);
            }
        
            if (proceed) {
                let temp: any = { ...branch };
        
                if (temp[propertyName] != propertyValue)
                {
                    hasChanges.current = true;
                    temp[propertyName] = propertyValue ?? null;
                    setBranch(temp);
                }
            }
        }
          
        function SaveBranch(close: boolean) {
            if (hasChanges.current)
            {
                if (openMode === OpenMode.EDIT)
                {
                    axios.put<BranchInfo>('Api/V1/Branch/', branch).then(
                        (result) => {
                            showSnackBar("Branch saved successfully.", "", "success");
                            hasChanges.current = false;
                            setSaved(true);
                            setBranch(result.data);
                            setOriginalBranch(result.data);
                            setOpen(!close);
                            setDialogOpen(false);
                            if (close)
                                props.onClose(true, result.data);
                        },
                        (error) => {
                            showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
                        }
                    );
                } 
                else
                {
                    axios.post<BranchInfo>('Api/V1/Branch/', branch).then(
                        (result) => {
                            showSnackBar("Branch created successfully.", "", "success");
                            hasChanges.current = false;
                            setSaved(true);
                            setBranch(result.data);
                            setOriginalBranch(result.data);
                            setOpenMode(OpenMode.EDIT);
                            setOpen(!close);
                            setDialogOpen(false);
                            if (close)
                                props.onClose(true, result.data);
                        },
                        (error) => {
                            showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
                        }
                    );
                }
            }
        }

        return (
            <div>
                <Modal open={open} onClose={() => closeModalCheck(true)}>
                    <div className={style.ModalMainNarrow}>
                        {!loading ? (
                            <div className={style.ModalBody}>
                                <Title pageTitle="Branch" />

                                <div className={style.ModalHeading}>
                                    <Typography variant={ tinyScreen ? "h6" : (smallScreen ? "h5" : "h4") }>Branch</Typography>
                                </div>

                                <div className={style.ModalContent}>
                                    <div className={style.ModalFields}>
                                        <TextField className={style.ModalContentFieldFullWidth}
                                            label="Region"
                                            value = {branch?.region ?? ""}
                                            onChange={(e) => OnBranchChanged("region", e.target.value.slice(0, 3))}
                                            size="small"
                                        />
                                       <TextField className={style.ModalContentFieldHalfWidth}
                                            label="Company Name (Eng)"
                                            value = {branch?.companyName1 ?? ""}
                                            onChange={(e) => OnBranchChanged("companyName1", e.target.value.slice(0, 50))}
                                            size="small"
                                        />
                                       <TextField className={style.ModalContentFieldHalfWidth}
                                            label="Company Name (Other)"
                                            value = {branch?.companyName2 ?? ""}
                                            onChange={(e) => OnBranchChanged("companyName2", e.target.value.slice(0, 50))}
                                            size="small"
                                        />
                                       <TextField className={style.ModalContentFieldHalfWidth}
                                            label="Address 1"
                                            value = {branch?.address1 ?? ""}
                                            multiline
                                            rows={3}
                                            onChange={(e) => OnBranchChanged("address1", e.target.value.slice(0, 80))}
                                            size="small"
                                        />
                                       <TextField className={style.ModalContentFieldHalfWidth}
                                            label="Address 2"
                                            value = {branch?.address2 ?? ""}
                                            multiline
                                            rows={3}
                                            onChange={(e) => OnBranchChanged("address2", e.target.value.slice(0, 80))}
                                            size="small"
                                        />                                                                                
                                       <TextField className={style.ModalContentFieldFullWidth}
                                            label="Phone"
                                            value = {branch?.phone ?? ""}
                                            onChange={(e) => OnBranchChanged("phone", e.target.value.slice(0, 25))}
                                            size="small"
                                        />
                                       <TextField className={style.ModalContentFieldFullWidth}
                                            label="Fax"
                                            value = {branch?.fax ?? ""}
                                            onChange={(e) => OnBranchChanged("fax", e.target.value.slice(0, 25))}
                                            size="small"
                                        />
                                       <TextField className={style.ModalContentFieldFullWidth}
                                            label="Japan Year"
                                            value = {branch?.japanYear ?? ""}
                                            onChange={(e) => OnBranchChanged("japanYear", e.target.value.slice(0, 2))}
                                            size="small"
                                        />
                                    </div>
                                </div>

                                <div className={style.ModalFooter}>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        className={style.margin}
                                        onClick={(e) => {
                                                SaveBranch(false);
                                            }
                                        }
                                        size="small"
                                        style={{ padding: 1}}
                                    >
                                        Save
                                    </Button>
                                    <div style={{ width: `100%`, minWidth: "10" }}></div>
                                    <Button
                                        variant="contained"
                                        className={style.margin}
                                        onClick={(e) => closeModalCheck(false)}
                                        size="small"
                                        style={{ padding: 1}}
                                    >
                                        Close
                                    </Button>                                
                                </div>
                            </div>
                        ) : (
                            <div style={{ textAlign: "center" }}>
                                <CircularProgress color="primary" />
                            </div>
                        )}
                    </div>
                </Modal>

                <SnackBarMessage {...snackSettings} />

                <GeneralDialog
                    title={"Save Changes?"}
                    message={
                        "Changes have been made, did you want to save before closing?"
                    }
                    open={dialogOpen}
                    options={[
                        {
                        text: "Save",
                        action: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => SaveBranch(true),
                        color: "secondary",
                        variant: "contained",
                        },
                        {
                        text: "No",
                        action: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => closeModal(),
                        color: "primary",
                        variant: "contained",
                        },
                    ]}
                />

            </div>
        );


    }

);


